import { httpJson } from '../../platform/abp/httpClient'
import { BUXUS_API_BASE_URL } from '../../platform/abp/config'

export type StudioPeopleHit = {
  userId: string
  email: string
  name: string
  tenantId: string | null
  tenantName: string | null
  /** Selectable row for an address that has no Buxus account yet. */
  inviteAsNew?: boolean
}

export type StudioInviteCheck = {
  result: 'eligible' | 'ineligible' | 'absent'
  hit: StudioPeopleHit | null
  tenantId: string | null
  tenantName: string | null
  reason: 'sso' | 'org-not-ready' | null
}

export type StudioPeopleCeiling = {
  userId: string
  email: string
  grantedPermissions: string[]
}

function unwrap<T>(body: unknown): T {
  const rec = body && typeof body === 'object' ? (body as Record<string, unknown>) : null
  if (rec && 'result' in rec && rec.result != null) return rec.result as T
  return body as T
}

function readHit(raw: unknown): StudioPeopleHit | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const userId = typeof row.userId === 'string' ? row.userId.trim() : ''
  const email = typeof row.email === 'string' ? row.email.trim() : ''
  if (!userId || !email) return null
  return {
    userId,
    email,
    name: typeof row.name === 'string' && row.name.trim() ? row.name.trim() : email,
    tenantId: typeof row.tenantId === 'string' && row.tenantId.trim() ? row.tenantId.trim() : null,
    tenantName:
      typeof row.tenantName === 'string' && row.tenantName.trim() ? row.tenantName.trim() : null,
  }
}

export async function searchStudioPeople(
  query: string,
  signal?: AbortSignal,
): Promise<StudioPeopleHit[]> {
  const q = query.trim()
  if (q.length < 3) return []
  const raw = await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/people?q=${encodeURIComponent(q)}`,
    { auth: true, signal },
  )
  const list = unwrap<unknown>(raw)
  if (!Array.isArray(list)) return []
  return list.flatMap((row) => {
    const hit = readHit(row)
    return hit ? [hit] : []
  })
}

export function isCompleteEmail(value: string): boolean {
  const text = value.trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
}

export async function checkInviteEmail(email: string, signal?: AbortSignal): Promise<StudioInviteCheck> {
  const raw = await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/people/invite-check?email=${encodeURIComponent(email.trim())}`,
    { auth: true, signal },
  )
  const row = readInviteCheckBody(raw)
  const resultRaw = typeof row?.result === 'string' ? row.result.trim().toLowerCase() : ''
  const result = resultRaw === 'eligible' || resultRaw === 'absent' ? resultRaw : 'ineligible'
  const hit = result === 'eligible' ? readHit(row?.hit) : null
  const tenantId =
    typeof row?.tenantId === 'string' && row.tenantId.trim() ? row.tenantId.trim() : null
  const tenantName =
    typeof row?.tenantName === 'string' && row.tenantName.trim() ? row.tenantName.trim() : null
  const reasonRaw = typeof row?.reason === 'string' ? row.reason.trim().toLowerCase() : ''
  const reason = reasonRaw === 'sso' || reasonRaw === 'org-not-ready' ? reasonRaw : null
  if (result === 'eligible' && !hit) {
    return { result: 'ineligible', hit: null, tenantId, tenantName, reason }
  }
  return { result, hit, tenantId, tenantName, reason }
}

/**
 * The verdict field is itself named `result`. Only peel an outer ABP wrapper when
 * that value is an object. A string verdict must stay on the payload.
 */
function readInviteCheckBody(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object') return null
  const body = raw as Record<string, unknown>
  if (body.result && typeof body.result === 'object') return body.result as Record<string, unknown>
  return body
}

export async function fetchStudioPeopleCeiling(userId: string): Promise<StudioPeopleCeiling | null> {
  const id = userId.trim()
  if (!id) return null
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/people/${encodeURIComponent(id)}`, {
    auth: true,
  })
  const row = unwrap<Record<string, unknown>>(raw)
  const granted = Array.isArray(row.grantedPermissions)
    ? row.grantedPermissions.filter((name): name is string => typeof name === 'string' && name.length > 0)
    : []
  return {
    userId: typeof row.userId === 'string' ? row.userId : id,
    email: typeof row.email === 'string' ? row.email : '',
    grantedPermissions: granted,
  }
}

export function sortStudioPeopleHits(
  hits: readonly StudioPeopleHit[],
  homeTenantId: string | null,
): StudioPeopleHit[] {
  const home = homeTenantId?.trim().toLowerCase() ?? ''
  return hits.slice().sort((a, b) => {
    const at = home && a.tenantId?.toLowerCase() === home ? 0 : 1
    const bt = home && b.tenantId?.toLowerCase() === home ? 0 : 1
    if (at !== bt) return at - bt
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
}
